import React, { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { BarChart3, TrendingUp, MousePointer, Eye, Target, AlertCircle, CheckCircle2, Zap, ChevronDown, ChevronUp } from 'lucide-react';
import { api, isUpgradeError } from '../context/AuthContext';
import { CreditCost } from './CreditMeter';
import { toast } from 'sonner';

const RATING_CONFIG = {
  'Excellent': { color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', barColor: 'bg-emerald-500' },
  'Good': { color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', barColor: 'bg-blue-500' },
  'Average': { color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', barColor: 'bg-amber-500' },
  'Needs Work': { color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', barColor: 'bg-red-500' },
};

const IMPACT_COLORS = {
  high: 'bg-red-100 text-red-700 border-red-200',
  medium: 'bg-amber-100 text-amber-700 border-amber-200',
  low: 'bg-slate-100 text-slate-600 border-slate-200',
};

const CATEGORY_ICONS = {
  content: '📝', timing: '⏰', media: '🖼', hashtags: '#', cta: '📢', format: '📐',
};

export const PerformancePrediction = ({ content, platforms, hashtags, scheduledHour, mediaUrls }) => {
  const [prediction, setPrediction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [showFactors, setShowFactors] = useState(false);

  const handlePredict = useCallback(async () => {
    if (!content?.trim()) {
      toast.error('Add some content before predicting performance');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/ai/predict-performance', {
        content,
        platforms: platforms || ['facebook'],
        hashtags: hashtags || [],
        scheduled_hour: scheduledHour ?? null,
        has_media: (mediaUrls || []).length > 0,
        media_count: (mediaUrls || []).length,
      });
      setPrediction(res.data);
      setExpanded(true);
    } catch (e) {
      if (!isUpgradeError(e)) toast.error(e.response?.data?.detail || 'Prediction failed');
    } finally {
      setLoading(false);
    }
  }, [content, platforms, hashtags, scheduledHour, mediaUrls]);

  if (!prediction && !loading) {
    return (
      <Card className="border-dashed border-slate-300 bg-slate-50/50" data-testid="performance-prediction-card">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-violet-500" />
              <span className="text-sm font-medium text-slate-700">Performance Prediction</span>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="border-violet-200 text-violet-600 hover:bg-violet-50 text-xs"
              onClick={handlePredict}
              disabled={loading || !content?.trim()}
              data-testid="predict-performance-btn"
            >
              <Zap className="h-3 w-3 mr-1" />
              Predict
              <CreditCost feature="predict" />
            </Button>
          </div>
          <p className="text-xs text-slate-400 mt-1">Analyze your post to predict engagement and get AI suggestions</p>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card className="border-violet-200 bg-violet-50/30" data-testid="performance-prediction-loading">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="animate-spin h-5 w-5 border-2 border-violet-500 border-t-transparent rounded-full" />
            <div>
              <p className="text-sm font-medium text-violet-700">Analyzing your post...</p>
              <p className="text-xs text-violet-500">Checking content quality, timing, and engagement potential</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const config = RATING_CONFIG[prediction.rating] || RATING_CONFIG['Average'];

  return (
    <Card className={`${config.border} border`} data-testid="performance-prediction-result">
      <CardHeader className="pb-2 pt-3 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-violet-500" />
            <CardTitle className="text-sm">Performance Prediction</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              className="text-xs h-7 px-2"
              onClick={handlePredict}
              disabled={loading}
            >
              Refresh
            </Button>
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-slate-400 hover:text-slate-600"
            >
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="px-4 pb-4 pt-0 space-y-3">
          {/* Score + Rating */}
          <div className="flex items-center gap-3">
            <div className={`relative w-16 h-16 rounded-full ${config.bg} flex items-center justify-center`}>
              <span className={`text-xl font-bold ${config.color}`}>{prediction.score}</span>
              <svg className="absolute inset-0" viewBox="0 0 64 64">
                <circle cx="32" cy="32" r="28" fill="none" stroke="currentColor" strokeWidth="3"
                  className="text-slate-200" />
                <circle cx="32" cy="32" r="28" fill="none" stroke="currentColor" strokeWidth="3"
                  className={config.color}
                  strokeDasharray={`${(prediction.score / 100) * 176} 176`}
                  strokeLinecap="round"
                  transform="rotate(-90 32 32)" />
              </svg>
            </div>
            <div>
              <Badge className={`${config.bg} ${config.color} border ${config.border} text-xs font-semibold`}>
                {prediction.rating}
              </Badge>
              <p className="text-xs text-slate-500 mt-1">
                {prediction.confidence}% confidence
                {prediction.source === 'ai' && <span className="text-violet-500"> &middot; AI enhanced</span>}
              </p>
            </div>
          </div>

          {/* Predicted Metrics */}
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-slate-50 rounded-lg p-2 flex items-center gap-2">
              <Eye className="h-3.5 w-3.5 text-blue-500" />
              <div>
                <p className="text-xs text-slate-500">Reach</p>
                <p className="text-sm font-semibold text-slate-800">{prediction.predicted_metrics.reach.toLocaleString()}</p>
              </div>
            </div>
            <div className="bg-slate-50 rounded-lg p-2 flex items-center gap-2">
              <TrendingUp className="h-3.5 w-3.5 text-pink-500" />
              <div>
                <p className="text-xs text-slate-500">Engagement</p>
                <p className="text-sm font-semibold text-slate-800">{prediction.predicted_metrics.engagement.toLocaleString()}</p>
              </div>
            </div>
            <div className="bg-slate-50 rounded-lg p-2 flex items-center gap-2">
              <MousePointer className="h-3.5 w-3.5 text-amber-500" />
              <div>
                <p className="text-xs text-slate-500">Clicks</p>
                <p className="text-sm font-semibold text-slate-800">{prediction.predicted_metrics.clicks.toLocaleString()}</p>
              </div>
            </div>
            <div className="bg-slate-50 rounded-lg p-2 flex items-center gap-2">
              <Target className="h-3.5 w-3.5 text-emerald-500" />
              <div>
                <p className="text-xs text-slate-500">Eng. Rate</p>
                <p className="text-sm font-semibold text-slate-800">{prediction.predicted_metrics.engagement_rate}%</p>
              </div>
            </div>
          </div>

          {/* Score Factors */}
          <div>
            <button
              className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 mb-1"
              onClick={() => setShowFactors(!showFactors)}
              data-testid="toggle-factors-btn"
            >
              {showFactors ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              Score breakdown ({prediction.score_factors?.length || 0} factors)
            </button>
            {showFactors && (
              <div className="space-y-1">
                {(prediction.score_factors || []).map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs py-1">
                    {f.positive ? (
                      <CheckCircle2 className="h-3 w-3 text-emerald-500 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="h-3 w-3 text-amber-500 flex-shrink-0" />
                    )}
                    <span className="text-slate-600 flex-1">{f.factor}</span>
                    <span className={`font-mono font-medium ${f.positive ? 'text-emerald-600' : 'text-amber-600'}`}>
                      {f.impact}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* AI Suggestions */}
          {prediction.suggestions?.length > 0 && (
            <div>
              <p className="text-xs font-medium text-slate-600 mb-2 flex items-center gap-1">
                <Zap className="h-3 w-3 text-violet-500" />
                Improvement suggestions
              </p>
              <div className="space-y-1.5">
                {prediction.suggestions.map((sug, i) => (
                  <div key={i} className="flex items-start gap-2 bg-slate-50 rounded-lg p-2">
                    <span className="text-sm flex-shrink-0">{CATEGORY_ICONS[sug.category] || '💡'}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-slate-700 leading-relaxed">{sug.tip}</p>
                    </div>
                    <Badge
                      variant="outline"
                      className={`text-[10px] px-1.5 py-0 flex-shrink-0 ${IMPACT_COLORS[sug.impact] || IMPACT_COLORS.low}`}
                    >
                      {sug.impact}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default PerformancePrediction;
